import type { Constructor, LitMixinBase } from './mixin-types';
import { property } from 'lit/decorators.js';
import { ContextProvider } from '@lit/context';
import { ActiveShopContext } from '../contexts/active-shop-context';
import { IShopAccess } from '../api/model/login-access-token';
import { posDb } from '../database/database';
import { UserProfile } from '../datatypes/user-profile';
import { setActiveCurrency } from '../datatypes/currency';

const ACTIVE_SHOP_KEY = 'active-shop-name';

// Provider mixin — paired with WithUserProfile on app-index. Owns the
// "currently selected shop" state, persists the shop name to IndexedDB so it
// survives reloads, and exposes setActiveShop() / clearActiveShop() to the
// rest of the app.
//
// Validation: when a shop name is loaded from IndexedDB, we re-check it
// against the current userProfile.shopAccess. If the user no longer has
// access (token rotated, ShopAccess shrunk, shop deleted, etc.), the active
// shop falls back to undefined and the routing logic in app-index sends the
// user back to /shop/select.
export const WithActiveShop = <T extends LitMixinBase>(superClass: T) => {
  class WithActiveShopClass extends superClass {
    private _activeShopProvider = new ContextProvider(this, {
      context: ActiveShopContext,
    });

    @property({ attribute: false })
    private _activeShop?: IShopAccess;

    public get activeShop(): IShopAccess | undefined {
      return this._activeShop;
    }

    private async _persistActiveShopName(name: string | undefined) {
      if (name) {
        await posDb.master.setRecord(ACTIVE_SHOP_KEY, name);
      } else {
        await posDb.master.deleteRecord(ACTIVE_SHOP_KEY);
      }
    }

    public async setActiveShop(shop: IShopAccess | undefined) {
      this._activeShop = shop;
      this._activeShopProvider.setValue(shop);
      // Phase J1 - point money/denomination rendering at this shop's currency
      // (falls back to the default when the shop or its currency is absent).
      setActiveCurrency(shop?.currency);
      await this._persistActiveShopName(shop?.shopName);
      this.requestUpdate();
    }

    public async clearActiveShop() {
      await this.setActiveShop(undefined);
    }

    // Resolves the persisted active shop against the user's current ShopAccess.
    // Drops it (and clears persistence) if the user no longer has access.
    public async restoreActiveShopFor(profile: UserProfile | undefined) {
      if (!profile) {
        await this.setActiveShop(undefined);
        return;
      }

      const access = profile.shopAccess.filter((s) => s.active);
      if (access.length === 0) {
        await this.setActiveShop(undefined);
        return;
      }

      const stored = await posDb.master.getRecord<string>(ACTIVE_SHOP_KEY);
      const storedName = stored?.value;
      const fromPersisted = storedName
        ? access.find(
            (s) => s.shopName.toLowerCase() === storedName.toLowerCase()
          )
        : undefined;

      if (fromPersisted) {
        await this.setActiveShop(fromPersisted);
        return;
      }

      // No stored choice or stored choice no longer valid. Auto-select if the
      // user has exactly one shop; otherwise clear so the picker UI takes over.
      if (access.length === 1) {
        await this.setActiveShop(access[0]);
      } else {
        await this.setActiveShop(undefined);
      }
    }
  }

  return WithActiveShopClass as Constructor<WithActiveShopInterface> & T;
};

export interface WithActiveShopInterface {
  activeShop?: IShopAccess;
  setActiveShop(shop: IShopAccess | undefined): Promise<void>;
  clearActiveShop(): Promise<void>;
  restoreActiveShopFor(profile: UserProfile | undefined): Promise<void>;
}
